import { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Share2 } from "lucide-react";
import { INSIGHTS, getInsight, formatDate } from "@/lib/insights";
import CTA from "@/components/sections/CTA";

export default function InsightArticle() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const item = getInsight(slug);
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

    useEffect(() => {
        if (!item) navigate("/resources", { replace: true });
    }, [item, navigate]);

    if (!item) return null;

    const idx = INSIGHTS.findIndex((i) => i.slug === slug);
    const next = INSIGHTS[(idx + 1) % INSIGHTS.length];

    return (
        <div data-testid={`article-${item.slug}`}>
            <motion.div
                style={{ scaleX }}
                className="fixed top-[76px] left-0 right-0 h-[2px] bg-gold origin-left z-40"
            />

            {/* Article hero */}
            <section className="relative pt-40 lg:pt-48 pb-16 lg:pb-24 overflow-hidden">
                <div className="absolute inset-0 -z-10">
                    <img src={item.hero} alt="" className="w-full h-full object-cover opacity-20" style={{ filter: "grayscale(60%) contrast(1.05)" }} />
                    <div className="absolute inset-0 bg-gradient-to-b from-paper/60 via-paper/90 to-paper"></div>
                    <div className="absolute inset-0 bg-grid opacity-30"></div>
                </div>

                <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
                    <Link to="/resources" className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-navy/50 hover:text-gold transition-colors mb-10" data-testid="back-to-insights">
                        <ArrowLeft size={14} /> All Resources
                    </Link>

                    <div className="flex flex-wrap items-center gap-3 mb-8">
                        <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold border border-gold/30 px-3 py-1.5">{item.type}</span>
                        <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-navy/60 border border-navy/15 px-3 py-1.5">{item.category}</span>
                        <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-navy/40">{item.chapter}</span>
                    </div>

                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9 }}
                        className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1] tracking-[-0.03em] text-navy max-w-[1000px]"
                    >
                        {item.title}
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.2 }}
                        className="mt-8 font-serif italic text-xl md:text-2xl text-navy/70 leading-snug max-w-[820px]"
                    >
                        {item.subtitle}
                    </motion.p>

                    <div className="mt-12 pt-8 border-t border-navy/10 grid grid-cols-2 md:grid-cols-4 gap-6">
                        <MetaCell label="Type" value={item.type} />
                        <MetaCell label="Read Time" value={`${item.readMinutes} minutes`} />
                        <MetaCell label="Author" value={item.authors[0].name} />
                        <MetaCell label="Focus" value={item.category} />
                    </div>
                </div>
            </section>

            {/* Summary */}
            <section className="relative border-t border-navy/10">
                <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        <div className="lg:col-span-3">
                            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-gold">Abstract</div>
                        </div>
                        <div className="lg:col-span-9">
                            <p className="font-serif text-2xl md:text-3xl leading-[1.25] text-navy/90">
                                {item.summary}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Body */}
            <article className="relative border-t border-navy/10">
                <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
                        <aside className="lg:col-span-3 hidden lg:block">
                            <div className="sticky top-32 space-y-6">
                                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-gold">Contents</div>
                                <ul className="space-y-3 text-sm text-navy/60">
                                    {item.body.filter((b) => b.type === "chapter").map((c, i) => (
                                        <li key={i} className="flex items-baseline gap-3">
                                            <span className="font-mono text-[10px] tracking-[0.22em] text-gold">{c.n}</span>
                                            <span className="font-mono text-[10px] tracking-[0.22em] uppercase">{c.label}</span>
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    onClick={() => navigator.clipboard?.writeText(window.location.href)}
                                    className="flex items-center gap-2 text-[10px] font-mono tracking-[0.22em] uppercase text-navy/50 hover:text-gold transition-colors"
                                    data-testid="share-article"
                                >
                                    <Share2 size={12} /> Copy Link
                                </button>
                            </div>
                        </aside>

                        <div className="lg:col-span-9 space-y-10 max-w-[780px]">
                            {item.body.map((block, i) => (
                                <ArticleBlock key={i} block={block} />
                            ))}

                            <div className="pt-16 mt-16 border-t border-navy/10">
                                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-navy/40 mb-3">Prepared By</div>
                                <div className="font-serif text-2xl text-navy">{item.authors[0].name}</div>
                                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-navy/50 mt-1">{item.authors[0].role}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </article>

            {/* Next article */}
            <section className="relative border-t border-navy/10 bg-white/40">
                <Link
                    to={`/resources/${next.slug}`}
                    className="group block max-w-[1500px] mx-auto px-6 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-6 hover:bg-white/70 transition-colors duration-500"
                    data-testid="next-article"
                >
                    <div className="lg:col-span-3 font-mono text-[10px] tracking-[0.24em] uppercase text-gold">Next Resource</div>
                    <div className="lg:col-span-8">
                        <h3 className="font-serif text-3xl md:text-5xl leading-[1.05] tracking-[-0.02em] text-navy group-hover:text-gold transition-colors duration-500">
                            {next.title}
                        </h3>
                        <p className="mt-4 text-navy/60">{next.subtitle}</p>
                    </div>
                    <div className="lg:col-span-1 flex lg:justify-end lg:items-start">
                        <ArrowUpRight size={22} className="text-navy/40 group-hover:text-gold group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500" />
                    </div>
                </Link>
            </section>

            <CTA />
        </div>
    );
}

function MetaCell({ label, value }) {
    return (
        <div>
            <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-navy/40 mb-2">{label}</div>
            <div className="text-sm text-navy">{value}</div>
        </div>
    );
}

function ArticleBlock({ block }) {
    switch (block.type) {
        case "chapter":
            return (
                <div className="pt-8 flex items-baseline gap-6" data-block="chapter">
                    <span className="chapter-num text-6xl md:text-7xl text-gold/90">{block.n}</span>
                    <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-navy/50">— {block.label}</span>
                </div>
            );
        case "h2":
            return <h2 className="font-serif text-3xl md:text-4xl leading-tight tracking-[-0.02em] text-navy">{block.text}</h2>;
        case "p":
            return <p className="text-navy/75 text-lg leading-[1.75] font-serif" style={{ fontWeight: 400 }}>{block.text}</p>;
        case "pullquote":
            return (
                <blockquote className="my-6 border-l-2 border-gold pl-6 md:pl-10">
                    <p className="font-serif italic text-2xl md:text-3xl text-navy leading-snug">"{block.text}"</p>
                </blockquote>
            );
        case "list":
            return (
                <ul className="space-y-4 pl-1">
                    {block.items.map((it, i) => (
                        <li key={i} className="flex gap-4 text-navy/75 text-lg leading-relaxed">
                            <span className="font-mono text-[11px] text-gold pt-2">— </span>
                            <span className="font-serif">{it}</span>
                        </li>
                    ))}
                </ul>
            );
        case "stats":
            return (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-navy/10 border border-navy/10 my-6">
                    {block.items.map((s, i) => (
                        <div key={i} className="bg-white p-5">
                            <div className="font-serif text-3xl md:text-4xl text-gold leading-none">{s.v}</div>
                            <div className="mt-3 font-mono text-[10px] tracking-[0.22em] uppercase text-navy/50 leading-tight">{s.k}</div>
                        </div>
                    ))}
                </div>
            );
        default:
            return null;
    }
}
